// shared-lib/room-fixtures.js
// Fixture-based room signals + section-label reconciliation. Pure, no SDK deps.
//
// Single source of truth for "what the objects in a room say its function is",
// used by three call sites that must agree:
//   1. Cloud-function ingest (`buildAlignedRooms`) — drop spurious section labels.
//   2. The kitchen layout gate (`kitchen-assembly/layoutKitchen.isKitchenRoom`).
//   3. The great-room program classifier (`great-room-assembly/roomFunction`).
//
// The motivating bug: RoomPlan's on-device section classifier sometimes tags a
// bathroom's long vanity + double-sink run a 'kitchen' section. A sink and
// cabinets are common to bathrooms too — the real kitchen discriminator is a
// COOKING appliance. So we use a *negative* kitchen test (bathroom fixtures
// present AND zero kitchen appliances) rather than trusting a single positive
// appliance detection, which keeps a stray mis-detected appliance from flipping
// a room's type.

const { getObjectType } = require('./fixture-types');

// Object types that positively identify a function.
const BATH_OBJECTS = new Set(['toilet', 'shower', 'bathtub', 'bath']);
// A cooking appliance is the real kitchen discriminator (a bare sink is not —
// bathrooms have sinks). sink + dishwasher together also counts as a kitchen.
const KITCHEN_APPLIANCES = new Set(['stove', 'range', 'oven', 'cooktop']);

/** Set of normalized object-type strings present in a room. */
function roomObjectTypes(room) {
  return new Set((room?.objects || []).map(getObjectType).filter(Boolean));
}

const hasAny = (types, set) => [...set].some((t) => types.has(t));

/** Does the room contain at least one bathroom fixture (toilet/shower/tub)? */
function hasBathroomFixtures(room, types = roomObjectTypes(room)) {
  return hasAny(types, BATH_OBJECTS);
}

/** Does the room contain a cooking appliance (or a sink + dishwasher pair)? */
function hasKitchenAppliances(room, types = roomObjectTypes(room)) {
  return (
    hasAny(types, KITCHEN_APPLIANCES) ||
    (types.has('sink') && types.has('dishwasher'))
  );
}

/**
 * Safe negative kitchen test. True when a room's fixtures say "bathroom, not
 * kitchen": at least one bathroom fixture AND zero kitchen appliances. This is
 * the signal used to reject a RoomPlan 'kitchen' section label / kitchen
 * classification on what is really a bathroom.
 */
function isSpuriousKitchen(room) {
  const types = roomObjectTypes(room);
  return hasBathroomFixtures(room, types) && !hasKitchenAppliances(room, types);
}

/**
 * Drop 'kitchen'-labeled sections from a room whose fixtures contradict them
 * (bathroom fixtures present, no kitchen appliances). Returns a NEW sections
 * array when it drops anything; returns the original reference unchanged when
 * there's nothing to reconcile (no sections, or the room isn't a
 * spurious-kitchen bathroom). Never mutates its input.
 */
function reconcileSections(room) {
  const sections = room?.sections;
  if (!Array.isArray(sections) || sections.length === 0) return sections;
  if (!isSpuriousKitchen(room)) return sections;
  return sections.filter(
    (s) => !(s?.label && s.label.toLowerCase().includes('kitchen'))
  );
}

module.exports = {
  BATH_OBJECTS,
  KITCHEN_APPLIANCES,
  roomObjectTypes,
  hasBathroomFixtures,
  hasKitchenAppliances,
  isSpuriousKitchen,
  reconcileSections,
};
